/**
 * NotificationBell.jsx - Componente de Notificações Push
 * CG Business ERP - P4
 * 
 * Mostra sino com badge de notificações pendentes
 * Dropdown com lista de alertas críticos
 * Botão para activar/desactivar notificações
 */

import { useState, useEffect } from 'react';
import { Bell, BellOff, BellRing, Settings, ExternalLink, AlertTriangle, Clock, Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useNotifications } from '@/hooks/useNotifications';
import { notificationsAPI } from '@/lib/api';
import { cn, formatDateTime } from '@/lib/utils';
import { useLanguage } from '@/i18n';

// Alert type icons and colors
const ALERT_CONFIG = {
  missed_checkpoint: { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Checkpoint em Falta' },
  no_opening: { icon: Ban, color: 'text-red-600', bg: 'bg-red-50', label: 'Sessão Não Aberta' },
  no_closing: { icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-50', label: 'Sessão Não Fechada' },
  cash_variance: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', label: 'Diferença de Caixa' }
};

export function NotificationBell() {
  const { t } = useLanguage();
  const {
    isSupported,
    permission,
    isEligible,
    isSubscribed,
    loading,
    pendingNotifications,
    subscribe,
    unsubscribe,
    dismissNotification
  } = useNotifications();
  
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [preferences, setPreferences] = useState({
    checkpoint_alerts: true,
    session_alerts: true,
    variance_alerts: true,
    reminder_interval_minutes: 15
  });
  const [savingPrefs, setSavingPrefs] = useState(false);
  
  // Load preferences
  useEffect(() => {
    if (isSubscribed) {
      notificationsAPI.getPreferences().then(res => {
        setPreferences(res.data);
      }).catch(() => {});
    }
  }, [isSubscribed]);
  
  // Handle subscription toggle
  const handleToggleSubscription = async () => {
    if (isSubscribed) {
      const result = await unsubscribe();
      if (result.success) {
        toast.success(t('notificationBell.disabled'));
      }
    } else {
      const result = await subscribe();
      if (result.success) {
        toast.success(t('notificationBell.enabled'));
      } else {
        toast.error(result.error || t('notificationBell.enableError'));
      }
    }
  };
  
  // Save preferences
  const handleSavePreferences = async () => {
    setSavingPrefs(true);
    try {
      await notificationsAPI.updatePreferences(preferences);
      toast.success(t('notificationBell.preferencesSaved'));
      setSettingsOpen(false);
    } catch (error) {
      toast.error(t('notificationBell.preferencesError'));
    } finally {
      setSavingPrefs(false);
    }
  };
  
  // Not eligible - don't show
  if (!isEligible && !loading) {
    return null;
  }
  
  const pendingCount = pendingNotifications.length;
  
  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon" 
            className="relative"
            data-testid="notification-bell"
          >
            {isSubscribed ? (
              pendingCount > 0 ? (
                <BellRing className="h-5 w-5 text-amber-600 animate-pulse" />
              ) : (
                <Bell className="h-5 w-5" />
              )
            ) : (
              <BellOff className="h-5 w-5 text-muted-foreground" />
            )}
            
            {/* Badge with count */}
            {pendingCount > 0 && (
              <Badge 
                variant="destructive" 
                className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
              >
                {pendingCount > 9 ? '9+' : pendingCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        
        <PopoverContent className="w-80 p-0" align="end">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b">
            <h4 className="font-semibold">{t('notificationBell.title')}</h4>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8"
              onClick={() => setSettingsOpen(true)}
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Subscription status */}
          {!isSubscribed && (
            <div className="p-4 bg-slate-50 border-b">
              <p className="text-sm text-muted-foreground mb-3">
                {t('notificationBell.enableDescription')}
              </p>
              <Button 
                onClick={handleToggleSubscription} 
                className="w-full"
                size="sm"
                data-testid="enable-notifications-btn"
              >
                <Bell className="h-4 w-4 mr-2" />
                {t('notificationBell.enableBrowser')}
              </Button>
              
              {permission === 'denied' && (
                <p className="text-xs text-red-600 mt-2">
                  {t('notificationBell.blockedWarning')}
                </p>
              )}
            </div>
          )}
          
          {/* Notifications list */}
          {isSubscribed && (
            <div className="max-h-64 overflow-y-auto">
              {pendingNotifications.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">
                  <Bell className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">{t('notificationBell.noPending')}</p>
                </div>
              ) : (
                pendingNotifications.map(notif => {
                  const config = ALERT_CONFIG[notif.alert_type] || ALERT_CONFIG.missed_checkpoint;
                  const Icon = config.icon;
                  
                  return (
                    <div 
                      key={notif.id}
                      className={cn("p-3 border-b hover:bg-slate-50 cursor-pointer", config.bg)}
                      onClick={() => {
                        window.location.href = '/cash?tab=alerts';
                      }}
                    >
                      <div className="flex gap-3">
                        <Icon className={cn("h-5 w-5 mt-0.5 flex-shrink-0", config.color)} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{notif.title}</p>
                          <p className="text-xs text-muted-foreground truncate">{notif.body}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDateTime(notif.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
          
          {/* Footer */}
          {isSubscribed && pendingNotifications.length > 0 && (
            <div className="p-2 border-t">
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full"
                onClick={() => window.location.href = '/cash?tab=alerts'}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                {t('notificationBell.viewAllAlerts')}
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
      
      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('notificationBell.preferencesTitle')}</DialogTitle>
            <DialogDescription>
              {t('notificationBell.preferencesDescription')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Subscription toggle */}
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-medium">{t('notificationBell.pushTitle')}</Label>
                <p className="text-sm text-muted-foreground">{t('notificationBell.pushDescription')}</p>
              </div>
              <Switch 
                checked={isSubscribed} 
                onCheckedChange={handleToggleSubscription}
              />
            </div>
            
            {isSubscribed && (
              <>
                <hr />
                
                {/* Alert type toggles */}
                <div className="flex items-center justify-between">
                  <div>
                    <Label>⏰ Checkpoints em Falta</Label>
                    <p className="text-xs text-muted-foreground">{t('notificationBell.checkpointDescription')}</p>
                  </div>
                  <Switch 
                    checked={preferences.checkpoint_alerts}
                    onCheckedChange={(checked) => setPreferences(p => ({...p, checkpoint_alerts: checked}))}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label>🚫 Sessões de Caixa</Label>
                    <p className="text-xs text-muted-foreground">{t('notificationBell.sessionDescription')}</p>
                  </div>
                  <Switch 
                    checked={preferences.session_alerts}
                    onCheckedChange={(checked) => setPreferences(p => ({...p, session_alerts: checked}))}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label>💰 Diferenças de Caixa</Label>
                    <p className="text-xs text-muted-foreground">{t('notificationBell.varianceDescription')}</p>
                  </div>
                  <Switch 
                    checked={preferences.variance_alerts}
                    onCheckedChange={(checked) => setPreferences(p => ({...p, variance_alerts: checked}))}
                  />
                </div>
                
                <hr />
                
                <Button 
                  onClick={handleSavePreferences} 
                  disabled={savingPrefs}
                  className="w-full"
                >
                  {savingPrefs ? t('notificationBell.saving') : t('notificationBell.savePreferences')}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default NotificationBell;
